import { useEffect, useMemo, useState } from "react";

import { useI18n } from "~/i18n";
import { fetchVaultItems } from "../../morrowVault";
import { fetchOrgMembers } from "../../orgMembers";
import { useMorrowClerkAuth, useShowOrgWorkspaceNav } from "../clerk/morrowClerkAuth";
import { MORROW_WORKSPACE_AGENTS } from "../workspace/morrowWorkspaceCatalog";
import { useTeamState } from "../workspace/morrowTeamStore";
import type { SidebarSearchCatalog } from "./sidebarUniversalSearch";

export function useSidebarSearchCatalog(active: boolean): SidebarSearchCatalog {
  const { t } = useI18n();
  const showOrg = useShowOrgWorkspaceNav();
  const { isLoaded: clerkLoaded, isSignedIn } = useMorrowClerkAuth();
  const team = useTeamState();
  const [vaultFiles, setVaultFiles] = useState<SidebarSearchCatalog["vaultFiles"]>([]);
  const [orgMembers, setOrgMembers] = useState<SidebarSearchCatalog["members"]>([]);

  const signedOut = clerkLoaded && !isSignedIn;

  useEffect(() => {
    if (!active || signedOut) return;
    let cancelled = false;
    void fetchVaultItems().then((items) => {
      if (cancelled) return;
      setVaultFiles(
        items.map((item) => ({
          id: item.id,
          title: item.name,
          subtitle: item.mimeType,
          searchTerms: [item.name, item.mimeType, item.sensitivity],
        })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [active, signedOut]);

  useEffect(() => {
    if (!active || signedOut || !showOrg) return;
    let cancelled = false;
    void fetchOrgMembers().then((members) => {
      if (cancelled || members === null) return;
      setOrgMembers(
        members.map((member) => ({
          key: member.email,
          title: member.title.trim() || member.email,
          subtitle: [member.functionRole, member.email].filter(Boolean).join(" · "),
          searchTerms: [member.email, member.title, member.functionRole, member.description],
        })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [active, showOrg, signedOut]);

  return useMemo(() => {
    const basePages: SidebarSearchCatalog["pages"] = [
      {
        route: "/activities",
        title: t("sidebar.activities"),
        searchTerms: [t("sidebar.activities"), "activities", "aktiviteler", "timeline"],
      },
      {
        route: "/agents",
        title: t("sidebar.agents"),
        searchTerms: [t("sidebar.agents"), "agents", "ajanlar", "mor"],
      },
      {
        route: "/vault",
        title: t("sidebar.vault"),
        searchTerms: [t("sidebar.vault"), "vault", "dosya", "file", "archive"],
      },
      {
        route: "/knowledge",
        title: t("sidebar.knowledge"),
        searchTerms: [
          t("sidebar.knowledge"),
          "knowledge",
          "bilgi",
          "profile",
          "preferences",
          "cognimemo",
          "memory",
          "bellek",
        ],
      },
      {
        route: "/settings",
        title: t("sidebar.settings"),
        searchTerms: [t("sidebar.settings"), "settings", "ayarlar", "preferences"],
      },
    ];

    const pages: SidebarSearchCatalog["pages"] = showOrg
      ? [
          {
            route: "/organization",
            title: t("sidebar.organization"),
            searchTerms: [t("sidebar.organization"), "organization", "şirket", "company"],
          },
          {
            route: "/team",
            title: t("sidebar.team"),
            searchTerms: [t("sidebar.team"), "team", "takım", "ekip", "members", "üye"],
          },
          ...basePages,
        ]
      : basePages;

    const agents = MORROW_WORKSPACE_AGENTS.map((agent) => ({
      id: agent.id,
      title: t(agent.roleKey),
      subtitle: t(agent.blurbKey),
      imageSrc: agent.imageSrc,
      searchTerms: [t(agent.roleKey), t(agent.blurbKey), agent.id],
    }));

    const knowledgeTabs: SidebarSearchCatalog["knowledgeTabs"] = [
      {
        tab: "profile",
        title: t("knowledge.tabAboutYou"),
        searchTerms: [t("knowledge.tabAboutYou"), "about", "profile", "hakkında", "facts"],
      },
      {
        tab: "behavior",
        title: t("knowledge.tabStyle"),
        searchTerms: [t("knowledge.tabStyle"), "style", "behavior", "stil", "tarz"],
      },
      {
        tab: "preferences",
        title: t("knowledge.tabPreferences"),
        searchTerms: [t("knowledge.tabPreferences"), "preferences", "tercihler"],
      },
    ];

    const localMembers: SidebarSearchCatalog["members"] = team.members.map((member) => ({
      key: member.id,
      title: member.name,
      subtitle: [member.title, member.role].filter(Boolean).join(" · "),
      searchTerms: [member.name, member.title, member.role, member.id],
    }));

    return {
      agents: signedOut ? [] : agents,
      members: signedOut ? [] : orgMembers.length > 0 ? orgMembers : localMembers,
      vaultFiles: signedOut ? [] : vaultFiles,
      knowledgeTabs: signedOut ? [] : knowledgeTabs,
      pages: signedOut ? pages.filter((page) => page.route === "/settings") : pages,
    };
  }, [orgMembers, showOrg, signedOut, t, team.members, vaultFiles]);
}
